import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3, ArrowLeft, TrendingUp, Clock, DollarSign, Eye, ShoppingCart,
  PieChart, Activity, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const AdAnalyticsDashboard = () => {
  const { formatCurrency } = useCountry();
  const [timeRange, setTimeRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [timeToSell, setTimeToSell] = useState(null);
  const [dailyPosts, setDailyPosts] = useState(null);
  const [conversionData, setConversionData] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchAllAnalytics();
  }, [timeRange]);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      const days = parseInt(timeRange);

      const [overviewRes, timeToSellRes, dailyPostsRes, conversionRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/ads/analytics/overview?days=${days}`, { headers }),
        axios.get(`${API_URL}/api/admin/ads/analytics/time-to-sell?days=${days}`, { headers }),
        axios.get(`${API_URL}/api/admin/ads/analytics/daily-posts?days=${days}`, { headers }),
        axios.get(`${API_URL}/api/admin/ads/analytics/conversion-rates?days=${days}`, { headers })
      ]);

      setAnalytics(overviewRes.data);
      setTimeToSell(timeToSellRes.data);
      setDailyPosts(dailyPostsRes.data);
      setConversionData(conversionRes.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/admin/ads">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <BarChart3 className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Ad Analytics</h1>
                <p className="text-emerald-100 text-sm">Performance insights and trends</p>
              </div>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40 bg-white/10 border-white/30 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20">
            <Activity className="h-12 w-12 text-gray-400 mx-auto animate-pulse" />
            <p className="text-gray-500 mt-4">Loading analytics...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Key Metrics */}
            {conversionData && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Ads Posted</p>
                        <p className="text-3xl font-bold">{formatNumber(conversionData.overall.total_ads)}</p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Activity className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Ads Sold</p>
                        <p className="text-3xl font-bold text-green-600">{formatNumber(conversionData.overall.sold_ads)}</p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-lg">
                        <ShoppingCart className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Sell-Through Rate</p>
                        <p className="text-3xl font-bold text-purple-600">{conversionData.overall.sell_through_rate}%</p>
                      </div>
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Views</p>
                        <p className="text-3xl font-bold">{formatNumber(conversionData.overall.total_views)}</p>
                      </div>
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Eye className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Posts Chart */}
              {dailyPosts && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Daily Ad Posts
                    </CardTitle>
                    <CardDescription>New ads posted per day</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 flex items-end gap-1">
                      {dailyPosts.daily_posts.slice(-14).map((day, index) => {
                        const maxCount = Math.max(...dailyPosts.daily_posts.map(d => d.count));
                        const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center">
                            <div
                              className="w-full bg-emerald-500 rounded-t hover:bg-emerald-600 transition-colors"
                              style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                              title={`${day.date}: ${day.count} ads`}
                            />
                            <p className="text-xs text-gray-400 mt-2 -rotate-45 origin-top-left">
                              {day.date.split('-').slice(1).join('/')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Time to Sell by Category */}
              {timeToSell && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Average Time to Sell
                    </CardTitle>
                    <CardDescription>Days from posting to sold, by category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {timeToSell.by_category.length === 0 ? (
                        <p className="text-center py-8 text-gray-400">No sold items data available</p>
                      ) : (
                        timeToSell.by_category.map((cat, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium capitalize">{cat.category?.replace(/-/g, ' ') || 'Unknown'}</p>
                              <p className="text-sm text-gray-500">{cat.total_sold} items sold</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-blue-600">{cat.avg_days_to_sell} days</p>
                              <p className="text-xs text-gray-400">min: {cat.min_days}d / max: {cat.max_days}d</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category Distribution */}
              {analytics && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Ads by Category
                    </CardTitle>
                    <CardDescription>Distribution of ads across categories</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {analytics.by_category.slice(0, 10).map((cat, index) => {
                        const total = analytics.by_category.reduce((sum, c) => sum + c.count, 0);
                        const percentage = total > 0 ? ((cat.count / total) * 100).toFixed(1) : 0;
                        return (
                          <div key={index} className="flex items-center gap-3">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium capitalize">{cat.category?.replace(/-/g, ' ') || 'Unknown'}</span>
                                <span className="text-sm text-gray-500">{cat.count} ({percentage}%)</span>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Conversion by Category */}
              {conversionData && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Conversion Rates
                    </CardTitle>
                    <CardDescription>Sell-through rate by category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {conversionData.by_category.slice(0, 10).map((cat, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium capitalize">{cat.category?.replace(/-/g, ' ') || 'Unknown'}</p>
                            <p className="text-sm text-gray-500">
                              {cat.sold} sold / {cat.total_ads} total
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge
                              className={`${cat.conversion_rate >= 50 ? 'bg-green-100 text-green-800' :
                                cat.conversion_rate >= 25 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'}`}
                            >
                              {cat.conversion_rate}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Location Stats */}
            {analytics && analytics.by_location && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Ads by Location
                  </CardTitle>
                  <CardDescription>Geographic distribution of listings</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {analytics.by_location.slice(0, 12).map((loc, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-gray-800">{loc.count}</p>
                        <p className="text-sm text-gray-500 truncate">{loc.location || 'Unknown'}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Performing Ads */}
            {analytics && analytics.top_performing && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Top Performing Ads
                  </CardTitle>
                  <CardDescription>Most viewed ads in this period</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">Title</th>
                          <th className="text-left p-3">Category</th>
                          <th className="text-left p-3">Price</th>
                          <th className="text-left p-3">Views</th>
                          <th className="text-left p-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.top_performing.slice(0, 10).map((ad, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{ad.title}</td>
                            <td className="p-3 capitalize">{ad.category?.replace(/-/g, ' ')}</td>
                            <td className="p-3">{formatCurrency(ad.price)}</td>
                            <td className="p-3">
                              <Badge variant="secondary">
                                <Eye className="h-3 w-3 mr-1" />
                                {ad.views?.toLocaleString()}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge className={`${ad.status === 'active' ? 'bg-green-500' :
                                ad.status === 'sold' ? 'bg-blue-500' : 'bg-gray-500'}`}>
                                {ad.status?.toUpperCase()}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdAnalyticsDashboard;
